import React, { useState, useEffect } from 'react';
import {
  IdentificationIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  DocumentTextIcon,
  ClipboardDocumentListIcon,
  CurrencyDollarIcon,
  EnvelopeIcon,
  MapPinIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline';
import apiClient, { exportAPI } from '../services/api';
import { useTranslation } from 'react-i18next';
import { marked } from 'marked';
import { ExportDropdownMenu } from '../components/ui/ExportDropdownMenu';

// Types
interface CountryInfo {
  code: string;
  name: string;
  regions_label: string;
}

interface RegionInfo {
  code: string;
  name: string;
}

interface ReasonInfo {
  id: string;
  name: string;
  description: string;
  documentation_required: string[];
}

interface StateRuleData {
  name: string;
  filing_fee: number;
  publication_required: boolean;
  publication_weeks: number;
  court_name: string;
  fingerprinting_required: boolean;
  background_check: boolean;
  residency_required_days: number;
  forms_needed: string[];
  notes: string;
}

interface EligibilityResult {
  eligible: boolean;
  reasons: string[];
  state_name: string;
  filing_fee: number;
  publication_required: boolean;
  publication_weeks: number;
  background_check_required: boolean;
  fingerprinting_required: boolean;
  residency_requirement_met: boolean;
  residency_days_required: number;
  estimated_timeline_weeks: number;
  next_steps: string[];
  notes: string;
}

interface CostItem {
  min: number;
  max: number;
  description: string;
}

interface CostEstimate {
  state: string;
  state_name: string;
  itemized_costs: Record<string, CostItem>;
  essential_total: { min: number; max: number };
  full_total: { min: number; max: number };
  notes: string[];
}

interface PetitionResponse {
  petition: string;
  state: string;
  state_name: string;
  reason: string;
  court_name: string;
  filing_fee: number;
  forms_needed: string[];
  filing_instructions: string[];
}

interface NotificationLetterResponse {
  letter: string;
  recipient_type: string;
  recipient_name: string;
  tips: string[];
}

interface DocumentChecklistItem {
  item: string;
  description: string;
  required: boolean;
}

interface FormWithUrl {
  name: string;
  url: string | null;
}

interface DocumentChecklist {
  state: string;
  state_name: string;
  checklist: DocumentChecklistItem[];
  forms?: FormWithUrl[];
  forms_required?: string[];
  tips: string[];
}

interface FilingStep {
  step: number;
  title: string;
  description: string;
}

interface FilingInstructions {
  state: string;
  state_name: string;
  court_name: string;
  steps: FilingStep[];
  filing_fee: number;
  publication_required: boolean;
  notes: string;
}

// Step definitions
const STEPS = ['state', 'reason', 'eligibility', 'petition', 'costs', 'notifications', 'checklist', 'instructions'];

// Notification letter types
const NOTIFICATION_TYPES = [
  { id: 'ssa', name: 'Social Security Administration' },
  { id: 'dmv', name: 'DMV / Motor Vehicle' },
  { id: 'passport', name: 'Passport Office' },
  { id: 'bank', name: 'Bank / Financial Institution' },
  { id: 'employer', name: 'Employer' },
  { id: 'irs', name: 'IRS' },
  { id: 'credit_bureaus', name: 'Credit Bureaus' },
];

export const NameChange: React.FC = () => {
  const { t, i18n } = useTranslation();

  // Current step
  const [currentStep, setCurrentStep] = useState(0);

  // Location selection (country + region)
  const [countries, setCountries] = useState<CountryInfo[]>([]);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [regions, setRegions] = useState<RegionInfo[]>([]);
  const [regionsLabel, setRegionsLabel] = useState('State');
  const [selectedRegion, setSelectedRegion] = useState('');
  const [stateRules, setStateRules] = useState<StateRuleData | null>(null);

  // Reason selection
  const [reasons, setReasons] = useState<ReasonInfo[]>([]);
  const [selectedReason, setSelectedReason] = useState('');

  // Personal information
  const [currentLegalName, setCurrentLegalName] = useState('');
  const [proposedNewName, setProposedNewName] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [placeOfBirth, setPlaceOfBirth] = useState('');
  const [residencyStartDate, setResidencyStartDate] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [reasonExplanation, setReasonExplanation] = useState('');

  // Results
  const [eligibilityResult, setEligibilityResult] = useState<EligibilityResult | null>(null);
  const [petitionResponse, setPetitionResponse] = useState<PetitionResponse | null>(null);
  const [costEstimate, setCostEstimate] = useState<CostEstimate | null>(null);
  const [documentChecklist, setDocumentChecklist] = useState<DocumentChecklist | null>(null);
  const [filingInstructions, setFilingInstructions] = useState<FilingInstructions | null>(null);

  // Notification letters
  const [selectedNotificationType, setSelectedNotificationType] = useState('ssa');
  const [notificationLetter, setNotificationLetter] = useState<NotificationLetterResponse | null>(null);
  const [generatedLetters, setGeneratedLetters] = useState<Record<string, NotificationLetterResponse>>({});

  // Cost calculation inputs
  const [numCertifiedCopies, setNumCertifiedCopies] = useState(5);
  const [needsFingerprinting, setNeedsFingerprinting] = useState(false);

  // Loading states
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load initial data
  useEffect(() => {
    loadCountries();
    loadReasons();
  }, []);

  // Load regions when country changes
  useEffect(() => {
    if (selectedCountry) {
      loadRegions(selectedCountry);
      setSelectedRegion('');
      setStateRules(null);
    }
  }, [selectedCountry]);

  // Load state rules when region changes
  useEffect(() => {
    if (selectedRegion) {
      loadStateRules();
    }
  }, [selectedRegion]);

  const loadCountries = async () => {
    try {
      const response = await apiClient.get('/name-change/countries', {
        params: { language: i18n.language }
      });
      setCountries(response.data.countries);
    } catch (err) {
      console.error('Failed to load countries:', err);
    }
  };

  const loadRegions = async (country: string) => {
    try {
      const response = await apiClient.get(`/name-change/regions/${country}`, {
        params: { language: i18n.language }
      });
      setRegions(response.data.regions);
      setRegionsLabel(response.data.regions_label || 'Region');
    } catch (err) {
      console.error('Failed to load regions:', err);
    }
  };

  const loadReasons = async () => {
    try {
      const response = await apiClient.get('/name-change/reasons', {
        params: { language: i18n.language }
      });
      setReasons(response.data.reasons || []);
    } catch (err) {
      console.error('Failed to load reasons:', err);
    }
  };

  const loadStateRules = async () => {
    try {
      const response = await apiClient.get(`/name-change/state-rules/${selectedRegion}`, {
        params: { language: i18n.language }
      });
      setStateRules(response.data.rules);
      setNeedsFingerprinting(response.data.rules.fingerprinting_required);
    } catch (err) {
      console.error('Failed to load state rules:', err);
    }
  };

  const handleCheckEligibility = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiClient.post('/name-change/check-eligibility', {
        state: selectedRegion,
        reason: selectedReason,
        current_name: currentLegalName,
        proposed_name: proposedNewName,
        residency_start_date: residencyStartDate || undefined,
        language: i18n.language
      });
      setEligibilityResult(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || t('nameChange:errors.eligibility', 'Failed to check eligibility'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleGeneratePetition = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiClient.post('/name-change/generate-petition', {
        state: selectedRegion,
        reason: selectedReason,
        current_legal_name: currentLegalName,
        new_name: proposedNewName,
        date_of_birth: dateOfBirth,
        place_of_birth: placeOfBirth,
        address: address,
        city: city,
        zip_code: zipCode,
        phone: phone || '',
        email: email || undefined,
        reason_explanation: reasonExplanation || `I am requesting a name change for ${reasons.find(r => r.id === selectedReason)?.name || 'personal reasons'}.`,
        is_minor: false,
        language: i18n.language
      });
      setPetitionResponse(response.data);
    } catch (err: any) {
      console.error('Petition generation error:', err);
      setError(err.response?.data?.detail || t('nameChange:errors.petition', 'Failed to generate petition'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCalculateCosts = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiClient.post('/name-change/calculate-costs', {
        state: selectedRegion,
        num_certified_copies: numCertifiedCopies,
        needs_fingerprinting: needsFingerprinting,
        language: i18n.language
      });
      setCostEstimate(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || t('nameChange:errors.costs', 'Failed to calculate costs'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateNotificationLetter = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiClient.post('/name-change/generate-notification-letter', {
        recipient_type: selectedNotificationType,
        current_legal_name: currentLegalName,
        new_name: proposedNewName,
        effective_date: new Date().toISOString().split('T')[0],
        address: `${address}, ${city}, ${selectedRegion} ${zipCode}`,
        language: i18n.language
      });
      setNotificationLetter(response.data);
      setGeneratedLetters(prev => ({
        ...prev,
        [selectedNotificationType]: response.data
      }));
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      const errorMessage = Array.isArray(detail)
        ? detail.map((e: any) => e.msg).join(', ')
        : (typeof detail === 'string' ? detail : t('nameChange:errors.notification', 'Failed to generate notification letter'));
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoadDocumentChecklist = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiClient.get(`/name-change/document-checklist/${selectedRegion}`, {
        params: { language: i18n.language }
      });
      setDocumentChecklist(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || t('nameChange:errors.checklist', 'Failed to load document checklist'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoadFilingInstructions = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiClient.get(`/name-change/filing-instructions/${selectedRegion}`, {
        params: { language: i18n.language }
      });
      setFilingInstructions(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || t('nameChange:errors.instructions', 'Failed to load filing instructions'));
    } finally {
      setIsLoading(false);
    }
  };

  const handlePetitionExport = async (format: 'word' | 'pdf' | 'text') => {
    if (!petitionResponse?.petition) return;
    await exportDocument(petitionResponse.petition, 'name-change-petition', format);
  };

  const handlePetitionPrint = () => {
    if (!petitionResponse?.petition) return;
    printDocument(petitionResponse.petition, t('nameChange:petition.title', 'Name Change Petition'));
  };

  const handleNotificationExport = async (format: 'word' | 'pdf' | 'text') => {
    if (!notificationLetter?.letter) return;
    await exportDocument(notificationLetter.letter, `name-change-notification-${selectedNotificationType}`, format);
  };

  const handleNotificationPrint = () => {
    if (!notificationLetter?.letter) return;
    const recipientName = NOTIFICATION_TYPES.find(t => t.id === selectedNotificationType)?.name || selectedNotificationType;
    printDocument(notificationLetter.letter, `Name Change Notification - ${recipientName}`);
  };

  const printDocument = (content: string, title: string) => {
    // Convert markdown to HTML using marked
    const htmlContent = marked(content, { async: false }) as string;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${title}</title>
          <style>
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
              line-height: 1.6;
              color: #1f2937;
              max-width: 800px;
              margin: 0 auto;
              padding: 40px 20px;
            }
            h1 { font-size: 24px; color: #1e40af; }
            h2 { font-size: 20px; color: #1f2937; margin-top: 24px; }
            h3 { font-size: 16px; color: #374151; margin-top: 16px; }
            p { margin: 12px 0; }
            ul, ol { margin: 8px 0; padding-left: 24px; }
            li { margin-bottom: 6px; }
            strong { font-weight: 600; }
            em { font-style: italic; }
            code { background: #f3f4f6; padding: 2px 6px; border-radius: 4px; font-family: monospace; }
            pre { background: #f3f4f6; padding: 12px; border-radius: 8px; overflow-x: auto; }
            pre code { background: none; padding: 0; }
            blockquote { border-left: 4px solid #2563eb; margin: 16px 0; padding-left: 16px; color: #666; }
            @media print {
              body { padding: 20px; }
            }
          </style>
        </head>
        <body>
          <div class="content">${htmlContent}</div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const exportDocument = async (content: string, baseFilename: string, format: 'word' | 'text' | 'pdf') => {
    try {
      const extensionMap: Record<string, string> = { word: 'docx', pdf: 'pdf', text: 'txt' };
      const extension = extensionMap[format];
      const filename = `${baseFilename}.${extension}`;

      const blob = await exportAPI.exportDocument({
        content: content,
        title: baseFilename.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        format: extension as 'docx' | 'pdf' | 'txt'
      });

      // Try to use File System Access API
      if ('showSaveFilePicker' in window) {
        try {
          const mimeTypes: Record<string, string> = {
            docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            pdf: 'application/pdf',
            txt: 'text/plain'
          };

          const descriptions: Record<string, string> = {
            word: 'Word Document',
            pdf: 'PDF Document',
            text: 'Text File'
          };

          const handle = await (window as any).showSaveFilePicker({
            suggestedName: filename,
            types: [{
              description: descriptions[format],
              accept: { [mimeTypes[extension]]: [`.${extension}`] }
            }]
          });

          const writable = await handle.createWritable();
          await writable.write(blob);
          await writable.close();
          return;
        } catch (err: any) {
          if (err.name !== 'AbortError') {
            console.warn('File System Access API failed, falling back to standard download:', err);
          } else {
            return;
          }
        }
      }

      // Fallback: Standard download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export error:', error);
      setError(t('nameChange:errors.exportFailed', 'Failed to export document. Please try again.'));
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0: // State selection
        return selectedRegion;
      case 1: // Reason selection
        return selectedReason && currentLegalName && proposedNewName;
      case 2: // Eligibility
        return eligibilityResult?.eligible;
      case 3: // Petition
        return petitionResponse;
      case 4: // Costs
        return costEstimate;
      case 5: // Notifications
        return Object.keys(generatedLetters).length > 0;
      case 6: // Checklist
        return documentChecklist;
      default:
        return true;
    }
  };

  const goToNextStep = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const goToPreviousStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const renderStepIndicator = () => {
    const stepLabels = [
      t('nameChange:steps.state', 'State'),
      t('nameChange:steps.reason', 'Reason'),
      t('nameChange:steps.eligibility', 'Eligibility'),
      t('nameChange:steps.petition', 'Petition'),
      t('nameChange:steps.costs', 'Costs'),
      t('nameChange:steps.notifications', 'Notifications'),
      t('nameChange:steps.checklist', 'Checklist'),
      t('nameChange:steps.instructions', 'Instructions'),
    ];

    return (
      <div className="mb-8">
        <div className="flex items-center justify-center overflow-x-auto">
          {stepLabels.map((label, index) => (
            <React.Fragment key={index}>
              <div className="flex flex-col items-center">
                <div
                  className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-xs sm:text-sm font-medium transition-colors ${
                    index < currentStep
                      ? 'bg-green-500 text-white'
                      : index === currentStep
                      ? 'bg-blue-600 dark:bg-blue-500 text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                  }`}
                >
                  {index < currentStep ? (
                    <CheckCircleIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                  ) : (
                    index + 1
                  )}
                </div>
                <span className="mt-2 text-xs text-gray-600 dark:text-gray-400 hidden md:block">
                  {label}
                </span>
              </div>
              {index < stepLabels.length - 1 && (
                <div
                  className={`w-4 sm:w-8 lg:w-12 h-1 mx-1 ${
                    index < currentStep ? 'bg-green-500' : 'bg-gray-200 dark:bg-gray-700'
                  }`}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  };

  const renderStateSelection = () => (
    <div className="space-y-6">
      {/* Country Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t('nameChange:location.selectCountry', 'Select Your Country')}
        </label>
        <select
          value={selectedCountry}
          onChange={(e) => setSelectedCountry(e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="">{t('nameChange:location.selectCountryPrompt', 'Select a country...')}</option>
          {countries.map((country) => (
            <option key={country.code} value={country.code}>{country.name}</option>
          ))}
        </select>
      </div>

      {/* Region Selection (only shown after country is selected) */}
      {selectedCountry && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('nameChange:location.selectRegion', `Select Your ${regionsLabel}`)}
          </label>
          <select
            value={selectedRegion}
            onChange={(e) => setSelectedRegion(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">{t('nameChange:location.selectRegionPrompt', `Select a ${regionsLabel.toLowerCase()}...`)}</option>
            {regions.map((region) => (
              <option key={region.code} value={region.code}>{region.name}</option>
            ))}
          </select>
        </div>
      )}

      {stateRules && (
        <div className="p-4 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg">
          <h3 className="font-medium text-blue-900 dark:text-blue-100 mb-3">
            {t('nameChange:state.rulesTitle', 'Name Change Requirements for')} {regions.find(r => r.code === selectedRegion)?.name}
          </h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-blue-700 dark:text-blue-300">{t('nameChange:state.filingFee', 'Filing Fee')}:</span>
              <span className="ml-2 font-medium text-blue-900 dark:text-blue-100">${stateRules.filing_fee}</span>
            </div>
            <div>
              <span className="text-blue-700 dark:text-blue-300">{t('nameChange:state.court', 'Court')}:</span>
              <span className="ml-2 font-medium text-blue-900 dark:text-blue-100">{stateRules.court_name}</span>
            </div>
            <div>
              <span className="text-blue-700 dark:text-blue-300">{t('nameChange:state.publication', 'Publication Required')}:</span>
              <span className="ml-2 font-medium text-blue-900 dark:text-blue-100">
                {stateRules.publication_required
                  ? `${t('nameChange:common.yes', 'Yes')} (${stateRules.publication_weeks} ${t('nameChange:common.weeks', 'weeks')})`
                  : t('nameChange:common.no', 'No')}
              </span>
            </div>
            <div>
              <span className="text-blue-700 dark:text-blue-300">{t('nameChange:state.residency', 'Residency Required')}:</span>
              <span className="ml-2 font-medium text-blue-900 dark:text-blue-100">{stateRules.residency_required_days} {t('nameChange:common.days', 'days')}</span>
            </div>
            <div>
              <span className="text-blue-700 dark:text-blue-300">{t('nameChange:state.fingerprinting', 'Fingerprinting')}:</span>
              <span className="ml-2 font-medium text-blue-900 dark:text-blue-100">
                {stateRules.fingerprinting_required ? t('nameChange:common.yes', 'Yes') : t('nameChange:common.no', 'No')}
              </span>
            </div>
            <div>
              <span className="text-blue-700 dark:text-blue-300">{t('nameChange:state.backgroundCheck', 'Background Check')}:</span>
              <span className="ml-2 font-medium text-blue-900 dark:text-blue-100">
                {stateRules.background_check ? t('nameChange:common.yes', 'Yes') : t('nameChange:common.no', 'No')}
              </span>
            </div>
          </div>
          {stateRules.notes && (
            <p className="mt-3 text-sm text-blue-800 dark:text-blue-200">{stateRules.notes}</p>
          )}
        </div>
      )}
    </div>
  );

  const renderReasonSelection = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t('nameChange:reason.selectReason', 'Reason for Name Change')}
        </label>
        <select
          value={selectedReason}
          onChange={(e) => setSelectedReason(e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="">{t('nameChange:reason.selectPrompt', 'Select a reason...')}</option>
          {reasons.map((reason) => (
            <option key={reason.id} value={reason.id}>{reason.name}</option>
          ))}
        </select>
      </div>

      {selectedReason && (
        <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {reasons.find(r => r.id === selectedReason)?.description}
          </p>
          {reasons.find(r => r.id === selectedReason)?.documentation_required && (
            <div className="mt-3">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('nameChange:reason.docsRequired', 'Documentation Required')}:
              </h4>
              <ul className="list-disc list-inside text-sm text-gray-600 dark:text-gray-400">
                {reasons.find(r => r.id === selectedReason)?.documentation_required.map((doc, i) => (
                  <li key={i}>{doc}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('nameChange:reason.currentName', 'Current Legal Name')}
          </label>
          <input
            type="text"
            value={currentLegalName}
            onChange={(e) => setCurrentLegalName(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder={t('nameChange:reason.currentNamePlaceholder', 'Your full current legal name')}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('nameChange:reason.proposedName', 'Proposed New Name')}
          </label>
          <input
            type="text"
            value={proposedNewName}
            onChange={(e) => setProposedNewName(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder={t('nameChange:reason.proposedNamePlaceholder', 'Your desired new legal name')}
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t('nameChange:reason.residencyDate', 'Date You Became a State Resident (Optional)')}
        </label>
        <input
          type="date"
          value={residencyStartDate}
          onChange={(e) => setResidencyStartDate(e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>
    </div>
  );

  const renderEligibilityCheck = () => (
    <div className="space-y-6">
      <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
        <h3 className="font-medium text-gray-900 dark:text-white mb-2">
          {t('nameChange:eligibility.summary', 'Summary')}
        </h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-500 dark:text-gray-400">{t('nameChange:eligibility.location', 'Location')}:</span>
            <span className="ml-2 text-gray-900 dark:text-white">{regions.find(r => r.code === selectedRegion)?.name}, {countries.find(c => c.code === selectedCountry)?.name}</span>
          </div>
          <div>
            <span className="text-gray-500 dark:text-gray-400">{t('nameChange:eligibility.reason', 'Reason')}:</span>
            <span className="ml-2 text-gray-900 dark:text-white">{reasons.find(r => r.id === selectedReason)?.name}</span>
          </div>
          <div>
            <span className="text-gray-500 dark:text-gray-400">{t('nameChange:eligibility.from', 'From')}:</span>
            <span className="ml-2 text-gray-900 dark:text-white">{currentLegalName}</span>
          </div>
          <div>
            <span className="text-gray-500 dark:text-gray-400">{t('nameChange:eligibility.to', 'To')}:</span>
            <span className="ml-2 text-gray-900 dark:text-white">{proposedNewName}</span>
          </div>
        </div>
      </div>

      <button
        onClick={handleCheckEligibility}
        disabled={isLoading}
        className="w-full px-6 py-3 bg-blue-600 dark:bg-blue-700 hover:bg-blue-700 dark:hover:bg-blue-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isLoading ? t('nameChange:common.checking', 'Checking...') : t('nameChange:eligibility.checkButton', 'Check Eligibility')}
      </button>

      {eligibilityResult && (
        <div className={`p-4 rounded-lg border ${
          eligibilityResult.eligible
            ? 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-800'
            : 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800'
        }`}>
          <div className="flex items-center mb-3">
            {eligibilityResult.eligible ? (
              <CheckCircleIcon className="h-6 w-6 text-green-600 dark:text-green-400 mr-2" />
            ) : (
              <ExclamationTriangleIcon className="h-6 w-6 text-red-600 dark:text-red-400 mr-2" />
            )}
            <span className={`font-medium ${
              eligibilityResult.eligible ? 'text-green-800 dark:text-green-200' : 'text-red-800 dark:text-red-200'
            }`}>
              {eligibilityResult.eligible
                ? t('nameChange:eligibility.eligible', 'You appear eligible for a name change')
                : t('nameChange:eligibility.notEligible', 'There may be issues with your eligibility')}
            </span>
          </div>

          <div className="mb-3 grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600 dark:text-gray-400">{t('nameChange:eligibility.filingFee', 'Filing Fee')}:</span>
              <span className="ml-2 font-medium text-gray-900 dark:text-white">${eligibilityResult.filing_fee}</span>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">{t('nameChange:eligibility.timeline', 'Est. Timeline')}:</span>
              <span className="ml-2 font-medium text-gray-900 dark:text-white">{eligibilityResult.estimated_timeline_weeks} {t('nameChange:common.weeks', 'weeks')}</span>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">{t('nameChange:eligibility.publication', 'Publication')}:</span>
              <span className="ml-2 font-medium text-gray-900 dark:text-white">
                {eligibilityResult.publication_required
                  ? `${t('nameChange:common.yes', 'Yes')} (${eligibilityResult.publication_weeks} ${t('nameChange:common.weeks', 'weeks')})`
                  : t('nameChange:common.no', 'No')}
              </span>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">{t('nameChange:eligibility.residencyMet', 'Residency Met')}:</span>
              <span className="ml-2 font-medium text-gray-900 dark:text-white">
                {eligibilityResult.residency_requirement_met ? t('nameChange:common.yes', 'Yes') : t('nameChange:common.no', 'No')}
              </span>
            </div>
          </div>

          {eligibilityResult.reasons && eligibilityResult.reasons.length > 0 && (
            <div className="mb-3">
              <h4 className={`font-medium mb-1 ${eligibilityResult.eligible ? 'text-green-800 dark:text-green-200' : 'text-red-800 dark:text-red-200'}`}>
                {eligibilityResult.eligible ? t('nameChange:eligibility.notes', 'Notes') : t('nameChange:eligibility.issues', 'Issues')}:
              </h4>
              <ul className={`list-disc list-inside text-sm ${eligibilityResult.eligible ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}>
                {eligibilityResult.reasons.map((reason, i) => (
                  <li key={i}>{reason}</li>
                ))}
              </ul>
            </div>
          )}

          {eligibilityResult.next_steps && eligibilityResult.next_steps.length > 0 && (
            <div>
              <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-1">{t('nameChange:eligibility.nextSteps', 'Next Steps')}:</h4>
              <ul className="list-disc list-inside text-sm text-gray-600 dark:text-gray-400">
                {eligibilityResult.next_steps.map((step, i) => (
                  <li key={i}>{step}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderPetitionGeneration = () => (
    <div className="space-y-6">
      <p className="text-gray-600 dark:text-gray-400">
        {t('nameChange:petition.description', 'Provide additional details to generate your name change petition.')}
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('nameChange:petition.dob', 'Date of Birth')} *
          </label>
          <input
            type="date"
            value={dateOfBirth}
            onChange={(e) => setDateOfBirth(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('nameChange:petition.placeOfBirth', 'Place of Birth (City, State)')} *
          </label>
          <input
            type="text"
            value={placeOfBirth}
            onChange={(e) => setPlaceOfBirth(e.target.value)}
            placeholder={t('nameChange:petition.placeOfBirthPlaceholder', 'e.g., Los Angeles, California')}
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t('nameChange:petition.address', 'Street Address')} *
        </label>
        <input
          type="text"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('nameChange:petition.city', 'City')} *
          </label>
          <input
            type="text"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('nameChange:petition.zip', 'ZIP Code')} *
          </label>
          <input
            type="text"
            value={zipCode}
            onChange={(e) => setZipCode(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('nameChange:petition.phone', 'Phone Number (Optional)')}
          </label>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('nameChange:petition.email', 'Email (Optional)')}
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t('nameChange:petition.reasonExplanation', 'Reason Explanation (Optional)')}
        </label>
        <textarea
          value={reasonExplanation}
          onChange={(e) => setReasonExplanation(e.target.value)}
          rows={3}
          placeholder={t('nameChange:petition.reasonExplanationPlaceholder', 'Briefly explain why you are requesting this name change...')}
          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      <button
        onClick={handleGeneratePetition}
        disabled={isLoading || !dateOfBirth || !placeOfBirth || !address || !city || !zipCode}
        className="w-full px-6 py-3 bg-blue-600 dark:bg-blue-700 hover:bg-blue-700 dark:hover:bg-blue-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isLoading ? t('nameChange:common.generating', 'Generating...') : t('nameChange:petition.generateButton', 'Generate Petition')}
      </button>

      {petitionResponse && (
        <div className="mt-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-gray-900 dark:text-white">
              {t('nameChange:petition.preview', 'Petition Preview')}
            </h3>
            <ExportDropdownMenu
              onExport={handlePetitionExport}
              onPrint={handlePetitionPrint}
              showPrint={true}
            />
          </div>

          <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
            <div className="space-y-2 text-sm mb-4">
              <div>
                <span className="text-gray-500 dark:text-gray-400">{t('nameChange:petition.court', 'Court')}:</span>
                <span className="ml-2 text-gray-900 dark:text-white">{petitionResponse.court_name}</span>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">{t('nameChange:petition.filingFee', 'Filing Fee')}:</span>
                <span className="ml-2 text-gray-900 dark:text-white">${petitionResponse.filing_fee}</span>
              </div>
            </div>
            <pre className="whitespace-pre-wrap text-sm text-gray-800 dark:text-gray-200 font-mono overflow-auto max-h-96">
              {petitionResponse.petition}
            </pre>
          </div>

          {petitionResponse.forms_needed && petitionResponse.forms_needed.length > 0 && (
            <div className="p-4 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg">
              <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">
                {t('nameChange:petition.formsNeeded', 'Forms You May Need')}
              </h4>
              <ul className="list-disc list-inside text-sm text-blue-800 dark:text-blue-200">
                {petitionResponse.forms_needed.map((form, i) => (
                  <li key={i}>{form}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderCostCalculation = () => (
    <div className="space-y-6">
      <p className="text-gray-600 dark:text-gray-400">
        {t('nameChange:costs.description', 'Calculate the estimated total costs for your name change.')}
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('nameChange:costs.certifiedCopies', 'Number of Certified Copies Needed')}
          </label>
          <input
            type="number"
            min="1"
            max="20"
            value={numCertifiedCopies}
            onChange={(e) => setNumCertifiedCopies(parseInt(e.target.value) || 1)}
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {t('nameChange:costs.certifiedCopiesHelp', 'Recommended: 5-10 copies for updating various records')}
          </p>
        </div>
        <div className="flex items-center">
          <input
            type="checkbox"
            id="needsFingerprinting"
            checked={needsFingerprinting}
            onChange={(e) => setNeedsFingerprinting(e.target.checked)}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded"
          />
          <label htmlFor="needsFingerprinting" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
            {t('nameChange:costs.fingerprinting', 'Include fingerprinting costs')}
            {stateRules?.fingerprinting_required && (
              <span className="ml-1 text-xs text-red-600 dark:text-red-400">
                ({t('nameChange:costs.requiredInState', 'Required in your state')})
              </span>
            )}
          </label>
        </div>
      </div>

      <button
        onClick={handleCalculateCosts}
        disabled={isLoading}
        className="w-full px-6 py-3 bg-blue-600 dark:bg-blue-700 hover:bg-blue-700 dark:hover:bg-blue-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isLoading ? t('nameChange:common.calculating', 'Calculating...') : t('nameChange:costs.calculateButton', 'Calculate Costs')}
      </button>

      {costEstimate && (
        <div className="p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg">
          <div className="mb-4">
            <h3 className="font-medium text-gray-900 dark:text-white mb-2">
              {t('nameChange:costs.for', 'Cost Estimate for')} {costEstimate.state_name}
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-sm text-gray-600 dark:text-gray-400">{t('nameChange:costs.essentialTotal', 'Essential Costs')}:</span>
                <p className="text-xl font-bold text-green-700 dark:text-green-300">
                  ${costEstimate.essential_total.min} - ${costEstimate.essential_total.max}
                </p>
              </div>
              <div>
                <span className="text-sm text-gray-600 dark:text-gray-400">{t('nameChange:costs.fullTotal', 'Full Total (incl. optional)')}:</span>
                <p className="text-xl font-bold text-green-700 dark:text-green-300">
                  ${costEstimate.full_total.min} - ${costEstimate.full_total.max}
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <h4 className="font-medium text-gray-700 dark:text-gray-300">{t('nameChange:costs.breakdown', 'Cost Breakdown')}:</h4>
            {Object.entries(costEstimate.itemized_costs).map(([key, item]) => (
              <div key={key} className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">{item.description}:</span>
                <span className="text-gray-900 dark:text-white">${item.min} - ${item.max}</span>
              </div>
            ))}
          </div>

          {costEstimate.notes && costEstimate.notes.length > 0 && (
            <div className="mt-4 pt-4 border-t border-green-200 dark:border-green-700">
              <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-1">{t('nameChange:costs.notes', 'Notes')}:</h4>
              <ul className="list-disc list-inside text-sm text-gray-600 dark:text-gray-400">
                {costEstimate.notes.map((note, i) => (
                  <li key={i}>{note}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderNotificationLetters = () => (
    <div className="space-y-6">
      <p className="text-gray-600 dark:text-gray-400">
        {t('nameChange:notifications.description', 'After your name change is approved, you\'ll need to notify various agencies and organizations. Generate notification letters below.')}
      </p>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t('nameChange:notifications.selectRecipient', 'Select Recipient')}
        </label>
        <select
          value={selectedNotificationType}
          onChange={(e) => {
            setSelectedNotificationType(e.target.value);
            setNotificationLetter(generatedLetters[e.target.value] || null);
          }}
          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          {NOTIFICATION_TYPES.map((type) => (
            <option key={type.id} value={type.id}>
              {type.name}
              {generatedLetters[type.id] ? ' (Generated)' : ''}
            </option>
          ))}
        </select>
      </div>

      <button
        onClick={handleGenerateNotificationLetter}
        disabled={isLoading || !currentLegalName || !proposedNewName}
        className="w-full px-6 py-3 bg-blue-600 dark:bg-blue-700 hover:bg-blue-700 dark:hover:bg-blue-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isLoading ? t('nameChange:common.generating', 'Generating...') : t('nameChange:notifications.generateButton', 'Generate Letter')}
      </button>

      {/* Generated letters summary */}
      <div className="flex flex-wrap gap-2">
        {NOTIFICATION_TYPES.map((type) => (
          <span
            key={type.id}
            className={`px-3 py-1 rounded-full text-xs font-medium ${
              generatedLetters[type.id]
                ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
            }`}
          >
            {type.name.split(' ')[0]}
            {generatedLetters[type.id] && <CheckCircleIcon className="h-3 w-3 inline ml-1" />}
          </span>
        ))}
      </div>

      {notificationLetter && (
        <div className="mt-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-gray-900 dark:text-white">
              {t('nameChange:notifications.preview', 'Letter Preview')} - {NOTIFICATION_TYPES.find(t => t.id === selectedNotificationType)?.name}
            </h3>
            <ExportDropdownMenu
              onExport={handleNotificationExport}
              onPrint={handleNotificationPrint}
              showPrint={true}
            />
          </div>

          <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
            <pre className="whitespace-pre-wrap text-sm text-gray-800 dark:text-gray-200 font-mono overflow-auto max-h-96">
              {notificationLetter.letter}
            </pre>
          </div>

          {notificationLetter.tips && notificationLetter.tips.length > 0 && (
            <div className="p-4 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg">
              <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">
                {t('nameChange:notifications.tips', 'Tips')}
              </h4>
              <ul className="list-disc list-inside text-sm text-blue-800 dark:text-blue-200">
                {notificationLetter.tips.map((tip, i) => (
                  <li key={i}>{tip}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderDocumentChecklist = () => (
    <div className="space-y-6">
      <p className="text-gray-600 dark:text-gray-400">
        {t('nameChange:checklist.description', 'Review the documents you\'ll need to gather for your name change petition.')}
      </p>

      {!documentChecklist && (
        <button
          onClick={handleLoadDocumentChecklist}
          disabled={isLoading}
          className="w-full px-6 py-3 bg-blue-600 dark:bg-blue-700 hover:bg-blue-700 dark:hover:bg-blue-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? t('nameChange:common.loading', 'Loading...') : t('nameChange:checklist.loadButton', 'Load Document Checklist')}
        </button>
      )}

      {documentChecklist && (
        <div className="space-y-6">
          <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <h3 className="font-medium text-gray-900 dark:text-white mb-1">
              {t('nameChange:checklist.for', 'Document Checklist for')} {documentChecklist.state_name}
            </h3>
          </div>

          {/* Official Court Forms Section */}
          {documentChecklist.forms && documentChecklist.forms.length > 0 && (
            <div className="p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg">
              <h3 className="font-medium text-green-900 dark:text-green-100 mb-3 flex items-center">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                {t('nameChange:checklist.officialForms', 'Official Court Forms')}
              </h3>
              <p className="text-sm text-green-800 dark:text-green-200 mb-3">
                {t('nameChange:checklist.formsDescription', 'Download these official state court forms to complete your name change petition:')}
              </p>
              <div className="space-y-2">
                {documentChecklist.forms.map((form, i) => (
                  <div key={i} className="flex items-center justify-between p-2 bg-white dark:bg-gray-800 rounded border border-green-100 dark:border-green-700">
                    <span className="font-medium text-gray-900 dark:text-white">{form.name}</span>
                    {form.url ? (
                      <a
                        href={form.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded transition-colors"
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        {t('nameChange:checklist.download', 'Download')}
                      </a>
                    ) : (
                      <span className="text-sm text-gray-500 dark:text-gray-400 italic">
                        {t('nameChange:checklist.contactCourt', 'Contact your local court')}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <h3 className="font-medium text-gray-900 dark:text-white mb-3">
              {t('nameChange:checklist.items', 'Required Documents')}
            </h3>
            <div className="space-y-2">
              {documentChecklist.checklist.map((item, i) => (
                <div key={i} className="flex items-start p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded"
                  />
                  <div className="ml-3">
                    <span className="font-medium text-gray-900 dark:text-white">{item.item}</span>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{item.description}</p>
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      item.required
                        ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200'
                        : 'bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-gray-200'
                    }`}>
                      {item.required ? t('nameChange:checklist.required', 'Required') : t('nameChange:checklist.optional', 'Optional')}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {documentChecklist.tips && documentChecklist.tips.length > 0 && (
            <div className="p-4 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg">
              <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">
                {t('nameChange:checklist.tips', 'Tips')}
              </h4>
              <ul className="list-disc list-inside text-sm text-blue-800 dark:text-blue-200">
                {documentChecklist.tips.map((tip, i) => (
                  <li key={i}>{tip}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderFilingInstructions = () => (
    <div className="space-y-6">
      <p className="text-gray-600 dark:text-gray-400">
        {t('nameChange:instructions.description', 'Follow these step-by-step instructions to file your name change petition.')}
      </p>

      {!filingInstructions && (
        <button
          onClick={handleLoadFilingInstructions}
          disabled={isLoading}
          className="w-full px-6 py-3 bg-blue-600 dark:bg-blue-700 hover:bg-blue-700 dark:hover:bg-blue-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? t('nameChange:common.loading', 'Loading...') : t('nameChange:instructions.loadButton', 'Load Filing Instructions')}
        </button>
      )}

      {filingInstructions && (
        <div className="space-y-6">
          <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <h3 className="font-medium text-gray-900 dark:text-white mb-3">
              {t('nameChange:instructions.overview', 'Overview')}
            </h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500 dark:text-gray-400">{t('nameChange:instructions.state', 'State')}:</span>
                <span className="ml-2 text-gray-900 dark:text-white">{filingInstructions.state_name}</span>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">{t('nameChange:instructions.court', 'Court')}:</span>
                <span className="ml-2 text-gray-900 dark:text-white">{filingInstructions.court_name}</span>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">{t('nameChange:instructions.filingFee', 'Filing Fee')}:</span>
                <span className="ml-2 text-gray-900 dark:text-white">${filingInstructions.filing_fee}</span>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">{t('nameChange:instructions.publication', 'Publication')}:</span>
                <span className="ml-2 text-gray-900 dark:text-white">
                  {filingInstructions.publication_required ? t('nameChange:common.yes', 'Yes') : t('nameChange:common.no', 'No')}
                </span>
              </div>
            </div>
          </div>

          <div>
            <h3 className="font-medium text-gray-900 dark:text-white mb-3">
              {t('nameChange:instructions.steps', 'Filing Steps')}
            </h3>
            <div className="space-y-3">
              {filingInstructions.steps?.map((step: FilingStep) => (
                <div key={step.step} className="flex items-start p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full flex items-center justify-center text-sm font-medium">
                    {step.step}
                  </span>
                  <div className="ml-3">
                    <span className="font-medium text-gray-900 dark:text-white">{step.title}</span>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {filingInstructions.notes && (
            <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
              <h4 className="font-medium text-yellow-900 dark:text-yellow-100 mb-2">
                {t('nameChange:instructions.notes', 'Important Notes')}
              </h4>
              <p className="text-sm text-yellow-800 dark:text-yellow-200">{filingInstructions.notes}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderCurrentStep = () => {
    try {
      switch (currentStep) {
        case 0:
          return renderStateSelection();
        case 1:
          return renderReasonSelection();
        case 2:
          return renderEligibilityCheck();
        case 3:
          return renderPetitionGeneration();
        case 4:
          return renderCostCalculation();
        case 5:
          return renderNotificationLetters();
        case 6:
          return renderDocumentChecklist();
        case 7:
          return renderFilingInstructions();
        default:
          return (
            <div className="p-6 text-center">
              <ExclamationTriangleIcon className="h-12 w-12 mx-auto text-yellow-500 mb-4" />
              <p className="text-gray-600 dark:text-gray-400">
                {t('nameChange:errors.unknownStep', 'Unknown step. Please try going back or starting over.')}
              </p>
            </div>
          );
      }
    } catch (err) {
      console.error('Error rendering step:', err);
      return (
        <div className="p-6 text-center">
          <ExclamationTriangleIcon className="h-12 w-12 mx-auto text-red-500 mb-4" />
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {t('nameChange:errors.renderError', 'An error occurred while displaying this step.')}
          </p>
          <button
            onClick={() => {
              setError(null);
              setCurrentStep(0);
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            {t('nameChange:navigation.startOver', 'Start Over')}
          </button>
        </div>
      );
    }
  };

  const getStepIcon = () => {
    const icons = [
      <MapPinIcon className="h-6 w-6" />,
      <QuestionMarkCircleIcon className="h-6 w-6" />,
      <CheckCircleIcon className="h-6 w-6" />,
      <DocumentTextIcon className="h-6 w-6" />,
      <CurrencyDollarIcon className="h-6 w-6" />,
      <EnvelopeIcon className="h-6 w-6" />,
      <ClipboardDocumentListIcon className="h-6 w-6" />,
      <DocumentTextIcon className="h-6 w-6" />,
    ];
    return icons[currentStep];
  };

  const getStepTitle = () => {
    const titles = [
      t('nameChange:stepTitles.state', 'Select Your State'),
      t('nameChange:stepTitles.reason', 'Reason for Name Change'),
      t('nameChange:stepTitles.eligibility', 'Check Eligibility'),
      t('nameChange:stepTitles.petition', 'Generate Petition'),
      t('nameChange:stepTitles.costs', 'Calculate Costs'),
      t('nameChange:stepTitles.notifications', 'Notification Letters'),
      t('nameChange:stepTitles.checklist', 'Document Checklist'),
      t('nameChange:stepTitles.instructions', 'Filing Instructions'),
    ];
    return titles[currentStep];
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8 text-center">
          <IdentificationIcon className="h-16 w-16 mx-auto mb-4 text-blue-600 dark:text-blue-400" />
          <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            {t('nameChange:title', 'Name Change Assistant')}
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            {t('nameChange:subtitle', 'Navigate the legal name change process step by step')}
          </p>
        </div>

        {/* Step Indicator */}
        {renderStepIndicator()}

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-800 dark:text-red-300">
            {error}
            <button
              onClick={() => setError(null)}
              className="ml-4 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"
            >
              {t('nameChange:common.dismiss', 'Dismiss')}
            </button>
          </div>
        )}

        {/* Main Content */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center mb-6">
            <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg text-blue-600 dark:text-blue-400 mr-4">
              {getStepIcon()}
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {getStepTitle()}
            </h2>
          </div>

          {renderCurrentStep()}
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between">
          <button
            onClick={goToPreviousStep}
            disabled={currentStep === 0}
            className="flex items-center px-6 py-3 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            {t('nameChange:navigation.previous', 'Previous')}
          </button>

          {currentStep < STEPS.length - 1 ? (
            <button
              onClick={goToNextStep}
              disabled={!canProceed()}
              className="flex items-center px-6 py-3 bg-blue-600 dark:bg-blue-700 hover:bg-blue-700 dark:hover:bg-blue-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('nameChange:navigation.next', 'Next')}
              <ArrowRightIcon className="h-5 w-5 ml-2" />
            </button>
          ) : (
            <button
              onClick={() => setCurrentStep(0)}
              className="flex items-center px-6 py-3 bg-green-600 dark:bg-green-700 hover:bg-green-700 dark:hover:bg-green-600 text-white rounded-lg font-medium transition-colors"
            >
              {t('nameChange:navigation.done', 'Done')}
            </button>
          )}
        </div>

        {/* Disclaimer */}
        <div className="mt-8 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
          <div className="flex items-start">
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5 mr-3 flex-shrink-0" />
            <div className="text-sm text-yellow-800 dark:text-yellow-200">
              <p className="font-medium mb-1">{t('nameChange:disclaimer.title', 'Important Disclaimer')}</p>
              <p>
                {t('nameChange:disclaimer.text', 'This tool provides general information about the legal name change process and generates draft documents for educational purposes. It does not constitute legal advice. Laws and procedures vary by jurisdiction and may change. Always verify information with your local court and consider consulting with a licensed attorney.')}
              </p>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};
